import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { sendEvent } from '../../utils/analytics';

const LinkHandler = ({
  className,
  children,
  role,
  link,
  dataTracking,
  analyticsData,
  data,
  tabIndex,
  onClick,
  ariaLabel,
}) => {
  if (!link) return null;

  const dataAttributes = {};
  if (data) {
    Object.keys(data).forEach((key) => {
      dataAttributes[`data-${key}`] = data[key];
    });
  }

  const tabindexAttr = tabIndex ? { tabIndex } : {};

  // Links can be part of a multi-ref with a max of 1, or an individual ref.
  const ref = Array.isArray(link) ? link[0] : link;

  const pushAnalyticsData = (url) => {
    if (analyticsData) {
      analyticsData.linkDestination = url;
      sendEvent(analyticsData);
    }
  };

  /**
   * External Link
   *
   * Simple a tag, always via the `Component - External Link` content type
   */

  if (
    ref.external ||
    (ref.internal && ref.internal.type === 'ContentfulComponentExternalLink')
  ) {
    const { URL, newTab } = ref;
    return (
      // eslint-disable-next-line react/jsx-no-target-blank
      <a
        href={URL}
        className={className}
        data-tracking={dataTracking}
        {...dataAttributes}
        rel={newTab ? 'noreferrer noopener' : 'noopener'}
        target={newTab ? '_blank' : '_self'}
        onClick={() => {
          pushAnalyticsData(URL);
          if (onClick) onClick();
        }}
        onKeyDown={(e) => {
          if (e.keydown === 13) {
            pushAnalyticsData(URL);
          }
        }}
        {...tabindexAttr}
      >
        {children}
      </a>
    );
  }

  /**
   * Link to assets
   *
   * Files are on Contentful instead of our domain, it behaves like an external link which does not use the Gatsby Link component.
   */

  if (ref.internal && ref.internal.type === 'ContentfulAsset') {
    const { url: URL } = ref.file;

    return (
      <a
        href={URL}
        className={className}
        data-tracking={dataTracking}
        {...dataAttributes}
        {...tabindexAttr}
        download
      >
        {children}
      </a>
    );
  }

  /**
   * Internal Link
   *
   * Internal Links always have a slug present. This can be set as an object with `slug` as a property
   * or come via a Contentful Reference, which sets the slug as a property.
   *
   * This uses the Gatsby Link component.
   *
   */

  if (ref.navigation && ref.navigation.path) {
    const { path } = ref.navigation;
    return (
      <Link
        role={role}
        to={path}
        className={className}
        data-tracking={dataTracking}
        data-internal-link
        {...dataAttributes}
        onClick={() => {
          pushAnalyticsData(path);
          if (onClick) onClick();
        }}
        onKeyDown={(e) => {
          if (e.keydown === 13) {
            pushAnalyticsData(path);
          }
        }}
        {...tabindexAttr}
        aria-label={ariaLabel}
      >
        {children}
      </Link>
    );
  }

  if (ref.path) {
    const { path } = ref;
    return (
      <Link
        to={path}
        className={className}
        data-tracking={dataTracking}
        data-internal-link
        {...dataAttributes}
        onClick={() => {
          pushAnalyticsData(path);
          if (onClick) onClick();
        }}
        onKeyDown={(e) => {
          if (e.keydown === 13) {
            pushAnalyticsData(path);
          }
        }}
        {...tabindexAttr}
        aria-label={ariaLabel}
      >
        {children}
      </Link>
    );
  }

  if (ref.slug) {
    const path = `/${ref.slug}`;
    return (
      <Link
        to={path}
        className={className}
        data-tracking={dataTracking}
        data-internal-link
        {...dataAttributes}
        onClick={() => {
          pushAnalyticsData(path);
          if (onClick) onClick();
        }}
        onKeyDown={(e) => {
          if (e.keydown === 13) {
            pushAnalyticsData(path);
          }
        }}
        {...tabindexAttr}
        aria-label={ariaLabel}
      >
        {children}
      </Link>
    );
  }

  return null;
};

const propExternalLink = PropTypes.shape({
  external: PropTypes.bool.isRequired,
  URL: PropTypes.string.isRequired,
  newTab: PropTypes.bool,
});

const propInternalLink = PropTypes.shape({
  internal: PropTypes.shape({
    type: PropTypes.string.isRequired,
  }).isRequired,
  slug: PropTypes.string,
  navigation: PropTypes.shape({
    path: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }),
});

const propNavigationItem = PropTypes.shape({
  navigation: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }),
});

const propJustSlug = PropTypes.shape({
  slug: PropTypes.string.isRequired,
});

export const propLink = PropTypes.oneOfType([
  PropTypes.arrayOf(PropTypes.oneOfType([propExternalLink, propInternalLink])),
  propInternalLink,
  propExternalLink,
  propNavigationItem,
  propJustSlug,
]);

LinkHandler.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  role: PropTypes.string,
  className: PropTypes.string,
  dataTracking: PropTypes.string,
  link: propLink,
  analyticsData: PropTypes.object,
  data: PropTypes.object,
  tabIndex: PropTypes.string,
  onClick: PropTypes.func,
  ariaLabel: PropTypes.string,
};

export default LinkHandler;
